import { useTranslation } from "@/hooks/useTranslation";
import {
  CATEGORY_COLORS,
  ColorScheme,
  OWNER_COLORS,
} from "@/lib/prompt-colors";
import { getIconForPrompt } from "@/lib/prompt-icons";
import {
  getPromptContent,
  getPromptTitle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import { PromptDTO } from "@shaarp/common";
import { LucideIcon, Star } from "lucide-react";
import { memo } from "react";

type PromptListItemProps = {
  prompt: PromptDTO;
  onClick: (prompt: PromptDTO) => void;
  isFavorite?: boolean;
  disabled?: boolean;
  showDescription?: boolean;
  className?: string;
};

type PromptStyleInfo = {
  Icon: LucideIcon;
  colors: ColorScheme;
  title: string;
  content: string;
};

/**
 * Get styling information for a prompt (icon, colors, title, content)
 */
const getPromptStyleInfo = (
  prompt: PromptDTO,
  lang: "fr" | "en",
): PromptStyleInfo => {
  const isPublic = prompt.ownerType === "public";
  const hasCategory = !!prompt.categoryId;

  // Get icon - always use getIconForPrompt for proper fallback handling
  const Icon = getIconForPrompt(prompt);

  // Get colors - use category colors for public prompts with category, owner colors otherwise
  const colors =
    isPublic && hasCategory
      ? CATEGORY_COLORS[prompt.categoryId!] || OWNER_COLORS.public
      : OWNER_COLORS[prompt.ownerType] || OWNER_COLORS.public;

  return {
    Icon,
    colors,
    title: getPromptTitle(prompt, lang),
    content: getPromptContent(prompt, lang),
  };
};

/**
 * Compact prompt item for lists and popovers
 * Used in: PromptsPopover (favorites & history), TargetedPromptsSuggestions
 */
function PromptListItem({
  prompt,
  onClick,
  isFavorite = false,
  disabled = false,
  showDescription = true,
  className,
}: PromptListItemProps) {
  const { i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);
  const { Icon, colors, title, content } = getPromptStyleInfo(prompt, lang);

  const handleClick = () => {
    if (!disabled) {
      onClick(prompt);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      onClick(prompt);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={cn(
        "w-full flex items-start gap-3 px-2 py-2 rounded-md text-left",
        "hover:bg-muted/60 transition-colors",
        "focus:outline-none focus:ring-2 focus:ring-primary/50",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
      aria-label={title}
    >
      <div
        className={cn(
          "flex items-center justify-center w-8 h-8 rounded-full shrink-0",
          colors.iconBg,
        )}
      >
        <Icon className={cn("w-4 h-4", colors.icon)} />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium truncate">{title}</p>
          {isFavorite && (
            <Star className="w-3 h-3 text-yellow-500 fill-yellow-500 shrink-0" />
          )}
        </div>
        {showDescription && (
          <p className="text-xs text-muted-foreground line-clamp-1">
            {content}
          </p>
        )}
      </div>
    </button>
  );
}

export default memo(PromptListItem);
